import styled from 'styled-components';
import PropertyInputWrapper from './PropertyInputWrapper';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Text } from 'foundations-components/components/Text';
import { useCardContextValue } from '../state/CardContext';
import { View } from 'foundations-components/components/View';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { usePropertyInputsContextValue } from '../state/PropertyInputsContext';
import PropertyDisplayWrapper from './PropertyDisplayWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_WIDTH = '50px';

/**
 * Container for the dynamic width input field
 * - Adjusts width based on content (either the label width when empty or value width when filled)
 * - Adds 2px padding to prevent text clipping
 * - Limits maximum width to 140px when empty to prevent oversized inputs
 * - Controls textarea visibility based on whether there's content or focus.
 */
const DynamicWidthLabelInputContainer = styled.div.withConfig({
  displayName: "DynamicWidthLabelInput__DynamicWidthLabelInputContainer",
  componentId: "sc-1x1rmzp-0"
})(["width:", " !important;max-width:", ";textarea,input{height:30px !important;opacity:", ";&:focus{opacity:1;min-width:", " !important;}}"], ({
  $valueWidth,
  $isValueEmpty,
  $labelWidth
}) => {
  if ($isValueEmpty) return `${$labelWidth + 2}px`;
  return $valueWidth ? `${$valueWidth + 2}px` : DEFAULT_WIDTH;
}, ({
  $isValueEmpty
}) => $isValueEmpty ? '140px' : '100%', ({
  $isValueEmpty
}) => $isValueEmpty ? 1 : 0, DEFAULT_WIDTH);

/**
 * Hidden element used to measure text width
 * - Positioned absolutely so it doesn't affect layout
 * - Uses nowrap to get accurate width measurement
 * - Zero height and hidden visibility ensure it's not visible to users
 * - Used to calculate both label and value widths for dynamic sizing
 */
const WidthMeasurer = styled.div.withConfig({
  displayName: "DynamicWidthLabelInput__WidthMeasurer",
  componentId: "sc-1x1rmzp-1"
})(["position:absolute;white-space:nowrap;height:0;visibility:hidden;opacity:0;"]);

/**
 * Overlay element that displays truncated text when input is not focused
 * - Positioned absolutely to sit on top of the input
 * - Blocks no pointer events so underlying input remains interactive
 * - Displays truncated text with ellipsis when content exceeds container width
 * - Maintains consistent line height for proper text alignment
 * - Provides visual indication that text is truncated (ellipsis)
 */
const TruncationOverlay = styled.div.withConfig({
  displayName: "DynamicWidthLabelInput__TruncationOverlay",
  componentId: "sc-1x1rmzp-2"
})(["position:absolute;top:1px;left:0;width:100%;height:100%;pointer-events:none;visibility:", ";*{line-height:30px !important;pointer-events:none !important;}"], ({
  $isValueEmpty
}) => $isValueEmpty ? 'hidden' : 'visible');
const DynamicWidthLabelInput = ({
  propertyName,
  isEditable
}) => {
  var _propertyDefinitionsR;
  const [labelWidth, setLabelWidth] = useState(0);
  const [valueWidth, setValueWidth] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const {
    asyncData: {
      propertyDefinitionsResult
    }
  } = useCardContextValue();
  const {
    getPropertyValue
  } = usePropertyInputsContextValue();
  const labelWidthMeasurerRef = useRef(null);
  const valueWidthMeasurerRef = useRef(null);
  const propertyValue = getPropertyValue(propertyName);
  const propertyLabel = (_propertyDefinitionsR = propertyDefinitionsResult.data) === null || _propertyDefinitionsR === void 0 || (_propertyDefinitionsR = _propertyDefinitionsR.get(propertyName)) === null || _propertyDefinitionsR === void 0 ? void 0 : _propertyDefinitionsR.property.label;
  useLayoutEffect(() => {
    var _valueWidthMeasurerRe, _valueWidthMeasurerRe2;
    setValueWidth((_valueWidthMeasurerRe = (_valueWidthMeasurerRe2 = valueWidthMeasurerRef.current) === null || _valueWidthMeasurerRe2 === void 0 ? void 0 : _valueWidthMeasurerRe2.clientWidth) !== null && _valueWidthMeasurerRe !== void 0 ? _valueWidthMeasurerRe : 0);
  }, [propertyValue]);
  useLayoutEffect(() => {
    var _labelWidthMeasurerRe, _labelWidthMeasurerRe2;
    setLabelWidth((_labelWidthMeasurerRe = (_labelWidthMeasurerRe2 = labelWidthMeasurerRef.current) === null || _labelWidthMeasurerRe2 === void 0 ? void 0 : _labelWidthMeasurerRe2.clientWidth) !== null && _labelWidthMeasurerRe !== void 0 ? _labelWidthMeasurerRe : 0);
  }, [propertyLabel]);
  const tooltipTitle = useMemo(() => isTruncated ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [propertyLabel, ":", /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(PropertyDisplayWrapper, {
      propertyName: propertyName
    })]
  }) : propertyLabel, [isTruncated, propertyLabel, propertyName]);
  return /*#__PURE__*/_jsxs(View, {
    overflow: "hidden",
    position: "relative",
    children: [/*#__PURE__*/_jsx(WidthMeasurer, {
      ref: labelWidthMeasurerRef,
      children: /*#__PURE__*/_jsx(Text, {
        typography: "heading-300",
        children: propertyLabel
      })
    }), /*#__PURE__*/_jsx(WidthMeasurer, {
      ref: valueWidthMeasurerRef,
      "data-test-id": "value-width-measurer",
      children: /*#__PURE__*/_jsx(Text, {
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(PropertyDisplayWrapper, {
          propertyName: propertyName
        })
      })
    }), /*#__PURE__*/_jsx(DynamicWidthLabelInputContainer, {
      $valueWidth: valueWidth,
      $labelWidth: labelWidth,
      $isValueEmpty: !propertyValue,
      children: /*#__PURE__*/_jsx(PropertyInputWrapper, {
        propertyName: propertyName,
        isEditable: isEditable,
        inputProps: {
          textWrap: 'nowrap',
          typography: 'heading-300'
        },
        onFocus: () => setIsInputFocused(true),
        onBlur: () => setIsInputFocused(false),
        tooltipTitle: tooltipTitle
      })
    }), !isInputFocused && /*#__PURE__*/_jsx(TruncationOverlay, {
      tabIndex: -1,
      "data-test-id": `truncation-overlay-${propertyName}`,
      $isValueEmpty: !propertyValue,
      children: /*#__PURE__*/_jsx(Text, {
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(TruncateString, {
          onTruncatedChange: e => setIsTruncated(e.target.value),
          children: /*#__PURE__*/_jsx(PropertyDisplayWrapper, {
            propertyName: propertyName
          })
        })
      })
    })]
  });
};
export default DynamicWidthLabelInput;